import { useState, useEffect, useRef } from 'react';
import { allSitters, cities } from '../../../mocks/sitters';
import { mockReviews } from '../../../mocks/reviews';

interface StatItem {
  value: number;
  suffix: string;
  label: string;
  icon: string;
}

function useCountUp(target: number, duration = 1800, start = false) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start || target === 0) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
      else setCount(target);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);

  return count;
}

function StatCard({ item, animate }: { item: StatItem; animate: boolean }) {
  const count = useCountUp(item.value, 1800, animate);

  return (
    <div className="relative flex flex-col items-center text-center group">
      <div className="w-16 h-16 mb-5 bg-[#81D8D0]/10 rounded-2xl flex items-center justify-center group-hover:bg-[#81D8D0]/20 transition-colors duration-300">
        <i className={`${item.icon} text-3xl text-[#81D8D0]`}></i>
      </div>
      <div className="flex items-end gap-1 mb-2">
        <span className="text-4xl sm:text-5xl font-black text-[#1B2A4A] tabular-nums leading-none">
          {count.toLocaleString()}
        </span>
        <span className="text-2xl sm:text-3xl font-black text-[#81D8D0] leading-none pb-0.5">
          {item.suffix}
        </span>
      </div>
      <p className="text-sm sm:text-base font-medium text-[#1B2A4A]/60">{item.label}</p>
    </div>
  );
}

export default function StatsSection() {
  const [animate, setAnimate] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setAnimate(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // 自動計算：データを追加するだけで数字が自動更新される
  const sitterCount = allSitters.length;
  const cityCount = cities.length;
  const reviewCount = mockReviews.filter((r) => r.is_published).length;
  const avgRating =
    mockReviews.reduce((sum, r) => sum + r.rating, 0) / mockReviews.length;
  const satisfactionRate = Math.round((avgRating / 5) * 100);

  const stats: StatItem[] = [
    {
      value: sitterCount,
      suffix: '名+',
      label: '登録シッター数',
      icon: 'ri-user-heart-line',
    },
    {
      value: cityCount,
      suffix: '都市',
      label: '対応都市数',
      icon: 'ri-map-pin-2-line',
    },
    {
      value: reviewCount,
      suffix: '件+',
      label: '口コミ投稿数',
      icon: 'ri-chat-smile-2-line',
    },
    {
      value: satisfactionRate,
      suffix: '%',
      label: 'オーナー満足度',
      icon: 'ri-heart-line',
    },
  ];

  return (
    <section ref={sectionRef} className="py-14 sm:py-20 bg-[#EEF4F7]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 sm:mb-16">
          <p className="text-xs font-semibold text-[#81D8D0] tracking-widest uppercase mb-3">NUMBERS</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1B2A4A]">
            PawBridgeの実績
          </h2>
          <p className="mt-3 text-sm sm:text-base text-[#1B2A4A]/60 max-w-lg mx-auto leading-relaxed">
            海外在住日本人オーナーとシッターをつなぐ、信頼のプラットフォーム
          </p>
        </div>

        <div className="bg-white rounded-3xl px-6 sm:px-10 py-10 sm:py-14">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-10">
            {stats.map((item, index) => (
              <div key={index} className="relative">
                {index < stats.length - 1 && (
                  <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 w-px h-16 bg-[#D4E8EC]"></div>
                )}
                <StatCard item={item} animate={animate} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
